import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Minus, Plus, ChevronRight, ChevronLeft, Lock, Bookmark, CheckCircle2, Mic, ZoomIn, ZoomOut, Expand, ListChecks, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { queueProgress, dropProgress, isNetworkError } from "@/lib/outbox";
import { PdfPage } from "@/components/PdfPage";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { RecordPanel } from "@/components/recording/RecordPanel";
import { ListenToggle, ListenBar } from "@/components/ListenAloud";
import { ReadingQuiz, useQuiz } from "@/components/quiz/ReadingQuiz";

const PAPER = { bg: "#FFFFFF", fg: "#1C1917" };

export const Reader = ({ bookId, onClose, saved, onProgress }) => {
  const { t, isRtl } = useLang();
  const [book, setBook] = useState(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(saved?.page ?? 0);
  const [finished, setFinished] = useState(Boolean(saved?.finished));
  const [size, setSize] = useState(() => Number(localStorage.getItem("yh_reader_size")) || 22);
  // Every book opens full screen with the page fitted to the screen; the reader can still switch it off.
  const [big, setBig] = useState(true);
  const fitRef = useRef(null);
  const autoFit = useRef(big);

  useEffect(() => {
    localStorage.setItem("yh_reader_size", String(size));
  }, [size]);
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  useEffect(() => {
    api.get(`/books/${bookId}`).then((r) => setBook(r.data)).catch((e) => {
      const s = e?.response?.status;
      setError(s === 401 ? "login" : s === 403 ? t.reader.pending : errMsg(e));
    });
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!book) return;
    api.put(`/progress/${bookId}`, { page })
      .then((r) => { setFinished(Boolean(r.data?.finished)); onProgress?.(bookId, r.data); dropProgress(bookId).catch(() => {}); })
      .catch((e) => {
        if (!isNetworkError(e)) return;
        // Offline: keep the page on this device and send it when the internet is back.
        const done = finished || page >= total - 1;
        setFinished(done);
        onProgress?.(bookId, { page, finished: done });
        queueProgress(bookId, page, done).catch(() => {});
      });
  }, [book, page, bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.__lenis?.stop();
    return () => { window.__lenis?.start(); };
  }, []);

  const th = PAPER;
  const scale = big ? 1.6 : 1;
  const pages = Array.isArray(book?.pages) ? book.pages : [];
  const isPdf = book?.type === "pdf";
  const compact = big && isPdf;
  const fit = (s = scale) => { if (fitRef.current) setSize(Math.min(44, (fitRef.current() * 22) / s)); };
  // Full-screen PDF: make the page fill the screen as soon as it is shown (and again when full screen is switched on).
  const onPageReady = () => {
    if (!autoFit.current) return;
    autoFit.current = false;
    requestAnimationFrame(() => fit());
  };
  const toggleBig = () => {
    const next = !big;
    setBig(next);
    if (next && isPdf) { autoFit.current = true; setTimeout(() => { if (autoFit.current) { autoFit.current = false; fit(1.6); } }, 350); }
  };
  const total = isPdf ? Math.max(book?.page_count ?? 1, 1) : pages.length;
  const [recordOpen, setRecordOpen] = useState(false);
  const [listen, setListen] = useState(false);
  const { user } = useAuth();
  const canRecord = user?.role === "user";
  const [quiz, setQuiz] = useQuiz(bookId, canRecord);
  const [quizOpen, setQuizOpen] = useState(false);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`fixed inset-0 z-[70] bg-[#1C1917]/80 flex items-center justify-center ${big ? "p-0" : "p-3 sm:p-6"}`} data-testid="reader-overlay">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.6 }}
        className={`w-full flex flex-col overflow-hidden shadow-2xl ${big ? "h-full max-w-none" : "max-w-3xl max-h-[92vh] rounded-sm"}`} style={{ background: th.bg, color: th.fg }} data-testid="reader-window">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-black/10 flex-wrap">
          <button onClick={onClose} data-testid="reader-close-btn" className="shrink-0 p-2 rounded-full bg-black/5 hover:bg-black/10" aria-label={t.reader.close}><X className="w-6 h-6" /></button>
          <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
            <button onClick={toggleBig} data-testid="reader-magnify-toggle" aria-pressed={big}
              className={`whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${big ? "bg-[#1C1917] text-white" : "border border-current hover:bg-black/10"}`}>
              {big ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />} {big ? t.reader.unmagnify : t.reader.magnify}
            </button>
            <span className="hidden sm:block w-px h-5 bg-current opacity-20 mx-1" />
            {isPdf && (
              <button onClick={() => fit()} data-testid="reader-fit-btn" aria-label={t.reader.fit} title={t.reader.fit}
                className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-current hover:bg-black/10 transition-colors">
                <Expand className="w-4 h-4" /> <span className="hidden sm:inline">{t.reader.fit}</span>
              </button>
            )}
            <span className="inline-flex items-center gap-1 shrink-0">
            <button onClick={() => setSize((s) => Math.max(isPdf ? 7 : 16, s - 2))} data-testid="reader-font-size-down" className="p-1.5 rounded-full hover:bg-black/10" aria-label="-"><Minus className="w-4 h-4" /></button>
            <button onClick={() => setSize((s) => Math.min(44, s + 2))} data-testid="reader-font-size-up" className="p-1.5 rounded-full hover:bg-black/10" aria-label="+"><Plus className="w-4 h-4" /></button>
            </span>
            <span className="hidden sm:block w-px h-5 bg-current opacity-20 mx-1" />
            <ListenToggle on={listen} onChange={(v) => { setListen(v); if (v) setRecordOpen(false); }} disabled={!book} />
            {canRecord && (
              <button onClick={() => setRecordOpen((v) => !v)} disabled={!book} data-testid="reader-record-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${recordOpen ? "bg-[#047857] text-white" : "border border-current hover:bg-black/10"} disabled:opacity-40`}>
                <Mic className="w-4 h-4" /> {t.rec.btn}
              </button>
            )}
            {quiz && (
              <button onClick={() => { setQuizOpen((v) => !v); setListen(false); setRecordOpen(false); }} data-testid="reader-quiz-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${quizOpen ? "bg-[#1C1917] text-white" : "bg-[#FBBF24] text-[#1C1917] hover:bg-[#F59E0B]"}`}>
                {quizOpen ? <BookOpen className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />} {quizOpen ? t.quiz.back : t.quiz.btn}
              </button>
            )}
          </div>
        </div>

        {listen && book && <ListenBar bookId={bookId} page={page} />}
        {recordOpen && book && <RecordPanel bookId={bookId} page={page} onStart={() => setListen(false)} />}

        <div className={`flex-1 overflow-y-auto ${isPdf ? (compact ? "px-2 sm:px-4 py-2" : "px-3 sm:px-6 py-6") : "px-6 sm:px-14 py-10"}`} data-lenis-prevent>
          {quizOpen && quiz ? (
            <ReadingQuiz bookId={bookId} quiz={quiz} onDone={setQuiz} />
          ) : error ? (
            <div className="text-center py-12 space-y-4" data-testid="reader-error">
              <Lock className="w-10 h-10 mx-auto opacity-60" />
              <p className="font-display text-2xl">{error === "login" ? t.reader.loginToRead : error}</p>
              {error === "login" ? <Link to="/login" className="inline-block underline" data-testid="reader-login-link">{t.reader.signIn}</Link> : null}
            </div>
          ) : !book ? (
            <p className="text-center opacity-70">{t.reader.loading}</p>
          ) : (
            <div dir={book.dir} className="relative">
              <div className={`flex flex-wrap items-center gap-2 ${compact ? "mb-2" : "mb-4"}`} dir={isRtl ? "rtl" : "ltr"}>
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#B45309] text-white rounded-full px-3 py-1" data-testid="reader-bookmark-badge">
                  <Bookmark className="w-3.5 h-3.5 fill-current" /> {t.reader.bookmark(page + 1)}
                </span>
                {finished && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#047857] text-white rounded-full px-3 py-1" data-testid="reader-finished-badge">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t.reader.finished}
                  </span>
                )}
              </div>
              {!isPdf && book.title && <h2 className={`text-3xl sm:text-4xl font-bold mb-8 ${book.dir === "rtl" ? "font-display" : "font-latin"}`} data-testid="reader-title">{book.title}</h2>}
              {isPdf ? (
                <PdfPage bookId={bookId} page={page} zoom={(size / 22) * scale} onError={setError} onFitReady={(fn) => { fitRef.current = fn; }} onPageReady={onPageReady} />
              ) : (
              <motion.p key={page} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                style={{ fontSize: Math.max(16, size) * scale, lineHeight: 1.9 }} className={book.dir === "rtl" ? "font-display" : "font-latin"} data-testid="reader-page-text">
                {pages[page]}
              </motion.p>
              )}
            </div>
          )}
        </div>

        {book && !quizOpen && (
          <div className="px-4 sm:px-6 py-4 border-t border-black/10 space-y-3">
            <div className="h-1 bg-current/10 rounded-full overflow-hidden" style={{ background: "rgba(127,127,127,0.2)" }}>
              <div className="h-full bg-[#B45309] transition-[width] duration-500" style={{ width: `${((page + 1) / total) * 100}%` }} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="reader-prev-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full hover:bg-black/10 disabled:opacity-30"><PrevIcon className="w-4 h-4" /> {t.reader.prev}</button>
              <span data-testid="reader-page-indicator">{page + 1} / {total}</span>
              <button disabled={page >= total - 1} onClick={() => setPage((p) => p + 1)} data-testid="reader-next-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full hover:bg-black/10 disabled:opacity-30">{t.reader.next} <NextIcon className="w-4 h-4" /></button>
            </div>
            {quiz && !quiz.attempt && page >= total - 1 && (
              <button onClick={() => setQuizOpen(true)} data-testid="reader-quiz-cta"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FBBF24] text-[#1C1917] py-2.5 font-bold hover:bg-[#F59E0B] transition-colors">
                <ListChecks className="w-5 h-5" /> {t.quiz.cta}
              </button>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
